import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} VariantOption
 * @property {string} name - The option name (e.g., "Color", "Size")
 * @property {number} position - The option position (1, 2, or 3)
 * @property {string[]} values - The available values for this option
 */

/**
 * @typedef {Object} VariantData
 * @property {number} id - The variant ID
 * @property {boolean} available - Whether the variant is available
 * @property {string|null} option1 - The first option value
 * @property {string|null} option2 - The second option value
 * @property {string|null} option3 - The third option value
 * @property {Array<string|null>} [options] - Legacy: The variant's option values as array
 */

/**
 * @typedef {Object} VariantOptionsData
 * @property {VariantOption[]} options - The product's options
 * @property {VariantData[]} variants - The product's variants
 * @property {number} [targetGenderOptionPosition] - The position of the target gender option
 * @property {string} [targetGenderValue] - The preferred target gender value
 * @property {number} [initialVariantId] - The initial variant ID when page loads
 */

/**
 * Manages variant selection logic with consistent normalization and target gender matching.
 * Single source of truth for variant finding logic.
 */
class VariantStateManager {
  /**
   * @param {VariantOptionsData | null} variantOptionsData - The variant options data
   */
  constructor(variantOptionsData) {
    this.#variantOptionsData = variantOptionsData;
  }

  /** @type {VariantOptionsData | null} */
  #variantOptionsData;

  /**
   * Normalizes a value using downcase and handleize (matching Liquid template logic).
   * This ensures consistent comparison between Liquid and JavaScript.
   * @param {string} value - The value to normalize
   * @returns {string} The normalized value
   */
  #normalizeValue(value) {
    if (!value) return '';
    // Match Liquid: downcase | handleize
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * Finds a variant that matches the swatch input while preserving target gender preference.
   * Priority order:
   * 1. Available variant with swatch value + target gender
   * 2. Any variant with swatch value + target gender
   * 3. Available variant with swatch value only
   * 4. Any variant with swatch value
   * @param {HTMLInputElement} swatchInput - The swatch input element
   * @param {boolean} [prioritizeTargetGender=true] - Whether to prioritize target gender variants
   * @returns {string | null} The variant ID or null if not found
   */
  findVariantForSwatch(swatchInput, prioritizeTargetGender = true) {
    if (!this.#variantOptionsData) return null;

    const { options, variants, targetGenderOptionPosition, targetGenderValue } = this.#variantOptionsData;
    if (!options || !variants) return null;

    // Get the new swatch value
    const newSwatchValue = swatchInput.value;
    if (!newSwatchValue) return null;

    // Find which option this is (Color, Size, etc)
    const swatchName = swatchInput.name || '';
    const swatchOptionName = swatchName.split('-')[0];

    // Find the position of this option
    let swatchOptionPosition = null;
    for (const option of options) {
      if (option.name.toLowerCase() === swatchOptionName?.toLowerCase()) {
        swatchOptionPosition = option.position;
        break;
      }
    }

    if (!swatchOptionPosition) return null;

    // Normalize target gender value for comparison (matching Liquid template)
    const normalizedTargetGender = targetGenderValue
      ? this.#normalizeValue(targetGenderValue)
      : null;

    // If we have a target gender preference and should prioritize it
    if (prioritizeTargetGender && targetGenderOptionPosition && normalizedTargetGender) {
      // First priority: Find an AVAILABLE variant with BOTH the new color AND the target gender
      for (const variant of variants) {
        const variantSwatchValue = /** @type {string|null|undefined} */ (
          variant[/** @type {keyof VariantData} */ (`option${swatchOptionPosition}`)]
        );
        const variantGenderValue = /** @type {string|null|undefined} */ (
          variant[/** @type {keyof VariantData} */ (`option${targetGenderOptionPosition}`)]
        );

        if (
          variantSwatchValue === newSwatchValue &&
          variantGenderValue &&
          this.#normalizeValue(variantGenderValue) === normalizedTargetGender &&
          variant.available
        ) {
          return variant.id.toString();
        }
      }

      // Second priority: Find ANY variant (even unavailable) with BOTH the new color AND the target gender
      for (const variant of variants) {
        const variantSwatchValue = /** @type {string|null|undefined} */ (
          variant[/** @type {keyof VariantData} */ (`option${swatchOptionPosition}`)]
        );
        const variantGenderValue = /** @type {string|null|undefined} */ (
          variant[/** @type {keyof VariantData} */ (`option${targetGenderOptionPosition}`)]
        );

        if (
          variantSwatchValue === newSwatchValue &&
          variantGenderValue &&
          this.#normalizeValue(variantGenderValue) === normalizedTargetGender
        ) {
          return variant.id.toString();
        }
      }
    }

    // Fallback: Find any available variant with the new swatch value (without target gender)
    for (const variant of variants) {
      const variantSwatchValue = /** @type {string|null|undefined} */ (
        variant[/** @type {keyof VariantData} */ (`option${swatchOptionPosition}`)]
      );

      if (variantSwatchValue === newSwatchValue && variant.available) {
        return variant.id.toString();
      }
    }

    // Final fallback: Return first variant with the swatch value (even if unavailable)
    for (const variant of variants) {
      const variantSwatchValue = /** @type {string|null|undefined} */ (
        variant[/** @type {keyof VariantData} */ (`option${swatchOptionPosition}`)]
      );

      if (variantSwatchValue === newSwatchValue) {
        return variant.id.toString();
      }
    }

    return null;
  }

  /**
   * Gets the initial variant ID matching Liquid template logic.
   * Returns the first available variant matching target gender, or falls back to any matching variant.
   * @returns {string | null} The initial variant ID or null
   */
  getInitialVariantId() {
    if (!this.#variantOptionsData) return null;

    const { variants, targetGenderOptionPosition, targetGenderValue, initialVariantId } =
      this.#variantOptionsData;

    // If we have an explicit initial variant ID, use it
    if (initialVariantId) {
      return initialVariantId.toString();
    }

    // If no target gender preference, return null (let Liquid template handle it)
    if (!targetGenderOptionPosition || !targetGenderValue) {
      return null;
    }

    const normalizedTargetGender = this.#normalizeValue(targetGenderValue);

    // First priority: Find first AVAILABLE variant matching target gender
    for (const variant of variants) {
      const variantGenderValue = /** @type {string|null|undefined} */ (
        variant[/** @type {keyof VariantData} */ (`option${targetGenderOptionPosition}`)]
      );

      if (
        variantGenderValue &&
        this.#normalizeValue(variantGenderValue) === normalizedTargetGender &&
        variant.available
      ) {
        return variant.id.toString();
      }
    }

    // Fallback: Find ANY variant matching target gender (even if unavailable)
    for (const variant of variants) {
      const variantGenderValue = /** @type {string|null|undefined} */ (
        variant[/** @type {keyof VariantData} */ (`option${targetGenderOptionPosition}`)]
      );

      if (
        variantGenderValue &&
        this.#normalizeValue(variantGenderValue) === normalizedTargetGender
      ) {
        return variant.id.toString();
      }
    }

    return null;
  }
}

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  // ─────────────────────────────────────────────────────────────────────────────
  // Static Configuration
  // ─────────────────────────────────────────────────────────────────────────────

  /** @type {number} Maximum attempts to wait for slideshow initialization */
  static #MAX_SLIDESHOW_WAIT_ATTEMPTS = 20;

  // ─────────────────────────────────────────────────────────────────────────────
  // Instance Properties
  // ─────────────────────────────────────────────────────────────────────────────

  requiredRefs = ['productCardLink'];

  /** @type {string | null} The variant ID that was explicitly clicked/selected */
  #selectedVariantId = null;

  /** @type {string | null} The variant ID currently being previewed (hovered) */
  #previewedVariantId = null;

  // ─────────────────────────────────────────────────────────────────────────────
  // Public Getters
  // ─────────────────────────────────────────────────────────────────────────────

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Click handler for product card navigation.
   * @param {Event} event - The click event.
   */
  #onClickNavigate = (event) => this.navigateToProduct(event);

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.#onClickNavigate);

    // Initialize the correct variant image if a specific variant is set via parent data attribute
    this.#initializeVariantFromParent();

    // Initialize the selected variant ID from the current state so hover reset works correctly
    this.#initializeSelectedVariantId();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    // Use requestAnimationFrame to ensure the component is fully rendered
    requestAnimationFrame(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#onClickNavigate);

    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);

    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    this.#resetVariantDebounced.cancel?.();
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Initializes the correct variant image based on a parent element's data-variant-id attribute.
   * This is used when rendering product cards from a metaobject variant list where a specific
   * variant should be displayed instead of the product's featured media.
   */
  #initializeVariantFromParent() {
    const parentWithVariantId = /** @type {HTMLElement | null} */ (this.closest('[data-variant-id]'));
    if (!parentWithVariantId) return;

    const variantId = parentWithVariantId.dataset.variantId;
    if (!variantId) return;

    this.#updateLinksForVariant(variantId);
    this.#waitForSlideshowAndSelect(variantId);
  }

  /**
   * Initializes the selected variant ID from the current state.
   * This ensures that when hover preview ends, the display resets to the correct variant.
   * Without this, the first hover-and-leave after page load would not restore properly
   * because #selectedVariantId would be null.
   */
  #initializeSelectedVariantId() {
    // First, try to get variant ID from the URL in the product card link
    const productCardLink = this.refs.productCardLink;
    if (productCardLink instanceof HTMLAnchorElement) {
      try {
        const url = new URL(productCardLink.href);
        const variantId = url.searchParams.get('variant');
        if (variantId) {
          this.#selectedVariantId = variantId;
          return;
        }
      } catch {
        // Ignore URL parsing errors
      }
    }

    // Second, try to get the initial variant ID from variant options data JSON
    // This matches the Liquid template logic for determining the initial variant
    const variantOptionsScript = this.querySelector('script[data-variant-options]');
    if (variantOptionsScript?.textContent) {
      try {
        const variantOptionsData = JSON.parse(variantOptionsScript.textContent);
        if (variantOptionsData.initialVariantId) {
          this.#selectedVariantId = variantOptionsData.initialVariantId.toString();
          return;
        }

        // If no explicit initialVariantId, use VariantStateManager logic to find one
        const variantStateManager = new VariantStateManager(variantOptionsData);
        const initialVariantId = variantStateManager.getInitialVariantId();
        if (initialVariantId) {
          this.#selectedVariantId = initialVariantId;
          return;
        }
      } catch {
        // Ignore JSON parsing errors
      }
    }

    // Fallback: Get variant ID from the currently checked swatch input
    const checkedSwatchInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"][name*="-swatch"]:checked')
    );
    if (checkedSwatchInput) {
      // Find the variant ID associated with this swatch value
      // The swatch may have data attributes pointing to the variant
      const variantId = checkedSwatchInput.dataset.variantId ||
        checkedSwatchInput.dataset.firstAvailableOrFirstVariantId;
      if (variantId) {
        this.#selectedVariantId = variantId;
        return;
      }
    }

    // Final fallback: Get from any checked input with variant-id data
    const checkedVariantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    if (checkedVariantInput?.dataset.variantId) {
      this.#selectedVariantId = checkedVariantInput.dataset.variantId;
    }
  }

  /**
   * Waits for the slideshow to be ready and then selects the variant slide.
   * Uses requestAnimationFrame for efficient polling until the slideshow is initialized.
   * @param {string} variantId - The variant ID to find and select.
   * @param {number} [attempts=0] - Number of retry attempts.
   */
  #waitForSlideshowAndSelect(variantId, attempts = 0) {
    const { slideshow } = this.refs;
    const slidesInDom = slideshow?.querySelectorAll('slideshow-slide');
    const slidesCount = slidesInDom?.length || 0;

    // If slideshow isn't ready yet, schedule a retry
    if (!slideshow || !slidesInDom || slidesCount === 0) {
      if (attempts < ProductCard.#MAX_SLIDESHOW_WAIT_ATTEMPTS) {
        requestAnimationFrame(() => {
          this.#waitForSlideshowAndSelect(variantId, attempts + 1);
        });
      }
      return;
    }

    this.#selectVariantSlideFromDom(variantId, slideshow, slidesInDom);
  }

  /**
   * Selects the slide that corresponds to the given variant ID using direct DOM query.
   * Also shows any variant metafield images associated with the selected variant.
   * @param {string} variantId - The variant ID to find and select.
   * @param {Element} slideshow - The slideshow element.
   * @param {NodeListOf<Element>} allSlides - All slide elements from DOM query.
   */
  #selectVariantSlideFromDom(variantId, slideshow, allSlides) {
    /** @type {Element | null} */
    let selectedSlide = null;

    for (const slide of allSlides) {
      const variantIdsAttr = slide.getAttribute('data-variant-ids');
      if (!variantIdsAttr) continue;

      const variantIds = variantIdsAttr.split(',');
      const isMetafieldImage = slide.hasAttribute('variant-metafield-image');

      // Find the main featured media slide for this variant (not metafield images)
      if (!isMetafieldImage && variantIds.includes(variantId)) {
        selectedSlide = slide;
        break;
      }
    }

    if (selectedSlide) {
      // Hide/show all variant images based on selection
      for (const otherSlide of allSlides) {
        const otherSlideEl = /** @type {HTMLElement} */ (otherSlide);
        if (otherSlide.hasAttribute('variant-image')) {
          const slideVariantIds = otherSlide.getAttribute('data-variant-ids');
          const isMetafieldImage = otherSlide.hasAttribute('variant-metafield-image');

          if (isMetafieldImage) {
            // Show metafield images only if they belong to the selected variant
            const shouldShow = slideVariantIds && slideVariantIds.split(',').includes(variantId);
            if (shouldShow) {
              otherSlideEl.hidden = false;
              otherSlide.setAttribute('reveal', ''); // Include in slideshow.slides
              otherSlide.setAttribute('aria-hidden', 'false');
            } else {
              otherSlideEl.hidden = true;
              otherSlide.removeAttribute('reveal');
              otherSlide.setAttribute('aria-hidden', 'true');
            }
          } else {
            // For regular variant images, show only the selected one
            if (otherSlide === selectedSlide) {
              otherSlideEl.hidden = false;
              otherSlide.setAttribute('reveal', '');
              otherSlide.setAttribute('aria-hidden', 'false');
            } else {
              otherSlideEl.hidden = true;
              otherSlide.removeAttribute('reveal');
              otherSlide.setAttribute('aria-hidden', 'true');
            }
          }
        }
      }

      // Select this slide in the slideshow after DOM updates are applied
      const slideId = selectedSlide.getAttribute('slide-id');
      if (slideId && 'select' in slideshow && typeof slideshow.select === 'function') {
        // Use requestAnimationFrame to ensure DOM changes are rendered before selecting
        requestAnimationFrame(() => {
          /** @type {import('slideshow').Slideshow} */ (slideshow).select(
          { id: slideId },
          undefined,
          { animate: false }
        );
        });
      }
    }
  }

  /**
   * Updates all product card links to include the specified variant ID.
   * @param {string} variantId - The variant ID to add to the URLs.
   */
  #updateLinksForVariant(variantId) {
    const { productCardLink } = this.refs;

    const updateLinkWithVariant = (/** @type {HTMLAnchorElement | Element | Element[] | null | undefined} */ link) => {
      if (!(link instanceof HTMLAnchorElement) || !link.href) return;
      const url = new URL(link.href);
      url.searchParams.set('variant', variantId);
      link.href = url.toString();
    };

    updateLinkWithVariant(productCardLink);
    updateLinkWithVariant(this.refs.productTitleLink);
    updateLinkWithVariant(this.refs.cardGalleryLink);
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const variantId = event.detail.resource?.id?.toString();
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Update metafield slides from the section response (if available)
    const slidesUpdated = this.#updateMetafieldSlides(event.detail.data.html, variantId);

    // Cancel any pending reset since we're selecting a new variant
    this.#resetVariantDebounced.cancel?.();

    // Store the selected variant ID
    this.#selectedVariantId = variantId ?? null;

    // If user is currently hovering (previewing), keep showing the preview.
    // Otherwise, update display to show the newly selected variant.
    // Use requestAnimationFrame when slides were updated to allow MutationObserver to process
    if (!this.#previewedVariantId) {
      if (slidesUpdated) {
        requestAnimationFrame(() => this.#updateDisplay());
      } else {
        this.#updateDisplay();
      }
    }
    // If previewing, the display will update when hover ends via #resetVariant

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Updates the display based on the current active variant (previewed or selected).
   * Always shows first image of the active variant.
   */
  #updateDisplay() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Get the active variant (previewed takes priority over selected)
    const activeVariantId = this.#activeVariantId;

    // If no active variant, fall back to initial state
    if (!activeVariantId) {
      const initialSlide = slideshow.initialSlide;
      const slideId = initialSlide?.getAttribute('slide-id');
      if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
        slideshow.select({ id: slideId }, undefined, { animate: false });
      }
      return;
    }

    // Query ALL slides directly from DOM
    const allSlides = slideshow.querySelectorAll('slideshow-slide');

    // Find the featured media slide for this variant (first non-metafield image)
    let featuredMediaSlideId = null;
    for (const slide of allSlides) {
      const slideVariantIds = slide.getAttribute('data-variant-ids');
      const isMetafieldImage = slide.hasAttribute('variant-metafield-image');
      const hasVariantImage = slide.hasAttribute('variant-image');

      if (hasVariantImage && !isMetafieldImage && slideVariantIds) {
        if (slideVariantIds.split(',').includes(activeVariantId)) {
          featuredMediaSlideId = slide.getAttribute('slide-id');
          break; // Always use first matching slide
        }
      }
    }

    // Update slide visibility based on active variant
    for (const slide of allSlides) {
      const slideEl = /** @type {HTMLElement} */ (slide);
      const hasVariantImage = slide.hasAttribute('variant-image');
      const slideVariantIds = slide.getAttribute('data-variant-ids');
      const isMetafieldImage = slide.hasAttribute('variant-metafield-image');

      if (!hasVariantImage) {
        // Non-variant slides (generic media) should always be visible
        continue;
      }

      // Determine if this slide should be shown
      const slideContainsVariant = slideVariantIds && slideVariantIds.split(',').includes(activeVariantId);
      const shouldShow = isMetafieldImage
        ? slideContainsVariant // Metafield images: show only for active variant
        : slideContainsVariant; // Regular variant images: show if contains variant

      // Update visibility
      slideEl.hidden = !shouldShow;
      slideEl.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

      // Add/remove 'reveal' attribute so slideshow.slides getter includes it
      if (shouldShow) {
        slideEl.setAttribute('reveal', '');
      } else {
        slideEl.removeAttribute('reveal');
      }
    }

    // Force slideshow to recalculate its visible slides and controls
    if (slideshow.hasAttribute('auto-hide-controls')) {
      slideshow.current = slideshow.current;
    }

    // Always select the first featured media slide (simplified logic)
    if (featuredMediaSlideId) {
      slideshow.select({ id: featuredMediaSlideId }, undefined, { animate: false });
    }
  }

  /**
   * Updates metafield slides from the section response.
   * When a variant changes, this replaces the existing metafield slides with
   * the ones for the newly selected variant from the fetched section HTML.
   * @param {Document|null} html - The parsed HTML from the section response.
   * @param {string|undefined} variantId - The ID of the selected variant.
   * @returns {boolean} True if slides were updated, false otherwise.
   */
  #updateMetafieldSlides(html, variantId) {
    if (!html || !variantId) return false;

    const { slideshow } = this.refs;
    if (!slideshow) return false;

    // Find the new metafield slides container from the response
    const newMetafieldContainer = html.querySelector('[data-metafield-slides-container]');
    if (!newMetafieldContainer) return false;

    // Get the scroller element where slides are rendered
    const scroller = slideshow.querySelector('[ref="scroller"]');
    if (!scroller) return false;

    // Remove existing metafield slides (they have variant-metafield-image attribute)
    const existingMetafieldSlides = scroller.querySelectorAll('[variant-metafield-image]');
    const hadExistingSlides = existingMetafieldSlides.length > 0;
    existingMetafieldSlides.forEach((slide) => slide.remove());

    // Get new metafield slides from the response
    const newSlides = newMetafieldContainer.querySelectorAll('slideshow-slide');
    if (newSlides.length === 0) return hadExistingSlides;

    // Insert new metafield slides at the end of the scroller
    newSlides.forEach((slide) => {
      const slideEl = /** @type {HTMLElement} */ (slide);
      slideEl.setAttribute('reveal', '');
      slideEl.setAttribute('aria-hidden', 'false');
      slideEl.hidden = false;
      scroller.appendChild(slideEl.cloneNode(true));
    });

    // Force slideshow to update its refs to include new slides
    if (typeof slideshow.updatedCallback === 'function') {
      slideshow.updatedCallback();
    }

    return true;
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /**
   * Gets the currently active variant ID.
   * Previewed variant (hover) takes priority over selected variant.
   * @returns {string | null}
   */
  get #activeVariantId() {
    return this.#previewedVariantId ?? this.#selectedVariantId;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    // No longer tracking previous slide index - always reset to first image on variant change
  };

  /**
   * Previews a variant by its media ID.
   * @param {string} mediaId - The media ID to preview.
   */
  previewVariant(mediaId) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Cancel any pending debounced reset since we're starting a new preview
    this.#resetVariantDebounced.cancel?.();

    // Find which variant owns this media
    const variantId = this.#findVariantIdFromMediaId(mediaId);

    // Set the previewed variant state
    this.#previewedVariantId = variantId;

    // Update display immediately
    this.#updateDisplay();

    // Also select the specific media slide
    slideshow.select({ id: mediaId }, undefined, { animate: false });
  }

  /**
   * Finds the variant ID that owns a given media ID.
   * @param {string} mediaId - The media ID to look up.
   * @returns {string | null} The variant ID or null if not found.
   */
  #findVariantIdFromMediaId(mediaId) {
    const { slideshow } = this.refs;
    if (!slideshow) return null;

    // Look through all slides to find which variant owns this media
    const allSlides = slideshow.querySelectorAll('slideshow-slide');
    for (const slide of allSlides) {
      const slideId = slide.getAttribute('slide-id');
      if (slideId === mediaId) {
        const variantIds = slide.getAttribute('data-variant-ids');
        if (variantIds) {
          // Return the first variant ID (media can belong to multiple variants)
          const firstVariantId = variantIds.split(',')[0];
          return firstVariantId ?? null;
        }
      }
    }
    return null;
  }

  /**
   * Previews the next image (hover over card).
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Cancel any pending debounced reset
    this.#resetVariantDebounced.cancel?.();

    // For image preview (not variant preview), just navigate to next slide
    slideshow.next(undefined, { animate: false });
    // Preload next image after navigation completes
    requestAnimationFrame(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image to the selected variant's image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      // No variant picker - just go to previous slide
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      // Use debounced resetVariant so it can be cancelled if user hovers again quickly
      this.#resetVariantDebounced();
    }
  }

  /**
   * Resets the display to the selected variant's images.
   * Called when hover ends.
   */
  #resetVariantNow() {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    // Clear the previewed variant - we're done hovering
    this.#previewedVariantId = null;

    // Update display to show the selected variant (or initial state if none selected)
    this.#updateDisplay();
  }

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant (called from template).
   * @param {PointerEvent} event - The pointer event.
   */
  resetVariant(event) {
    if (event?.pointerType && event.pointerType !== 'mouse') return;
    this.#resetVariantDebounced();
  }

  /**
   * Resets the preview immediately without debounce.
   * Use this when definitively leaving a swatch area to ensure the reset happens.
   */
  resetPreviewImmediately() {
    // Cancel any pending debounced reset to avoid double execution
    this.#resetVariantDebounced.cancel?.();
    // Reset immediately
    this.#resetVariantNow();
  }

  /**
   * Private debounced reset variant handler.
   */
  #resetVariantDebounced = debounce(() => this.#resetVariantNow(), 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /** @type {VariantOptionsData | null} Cached variant options data from the product card */
  #variantOptionsData = null;

  /** @type {VariantStateManager | null} Variant state manager instance */
  #variantStateManager = null;

  /** @type {ReturnType<typeof debounce> | null} Debounced hover handler */
  #hoverDebounced = null;

  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Cache variant options data for preserving other options during swatch changes
    this.#cacheVariantOptionsData();

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));

    // Note: Carousel reset is now handled immediately in #handleSwatchHoverDebounced()
    // before fetching, so no need to reset again after update completes

    // Add hover listeners to all swatch inputs for preview functionality
    this.#setupSwatchHoverListeners();

    // Select the initial swatch that matches the current variant
    this.#selectInitialSwatch();
  }

  /**
   * Caches variant options data from the product card's JSON script.
   * This data allows us to find variants that preserve other option values when changing one option.
   */
  #cacheVariantOptionsData() {
    const script = this.parentProductCard?.querySelector('script[data-variant-options]');
    if (script?.textContent) {
      try {
        this.#variantOptionsData = JSON.parse(script.textContent);
        // Initialize VariantStateManager with the cached data
        this.#variantStateManager = new VariantStateManager(this.#variantOptionsData);
      } catch (e) {
        // Silently fail - variant options data is optional
      }
    }
  }

  /**
   * Sets up hover event listeners on swatch inputs for preview functionality.
   * On hover, it will fetch and update the product card with the variant that would be selected.
   */
  #setupSwatchHoverListeners() {
    const swatchInputs = this.querySelectorAll('input[type="radio"][name*="-swatch"]');

    // Create debounced hover handler (150ms delay as per plan)
    this.#hoverDebounced = debounce((swatchInput) => {
      this.#handleSwatchHoverDebounced(swatchInput);
    }, 150);

    swatchInputs.forEach(input => {
      // Cast to HTMLInputElement since we know these are input elements
      const swatchInput = /** @type {HTMLInputElement} */ (input);

      // Add mouseenter listener for hover preview (debounced)
      swatchInput.parentElement?.addEventListener('mouseenter', () => {
        // Cancel any pending leave handler
        this.#leaveDebounced?.cancel?.();
        this.#hoverDebounced?.(swatchInput);
      });

      // Add mouseleave listener to restore original state
      swatchInput.parentElement?.addEventListener('mouseleave', () => {
        // Cancel pending hover
        this.#hoverDebounced?.cancel?.();
        this.#handleSwatchLeave(swatchInput);
      });
    });
  }

  /** @type {ReturnType<typeof debounce> | null} Debounced leave handler */
  #leaveDebounced = null;

  /** @type {HTMLInputElement | null} The swatch input currently being previewed */
  previewedSwatch = null;

  /** @type {string | null} The original variant ID before preview started */
  #originalVariantBeforePreview = null;

  /** @type {boolean} Flag to indicate we're processing a real selection (not preview) */
  #isSelectingVariant = false;

  /** @type {boolean} Track if overflow list is expanded */
  #overflowListExpanded = false;

  /**
   * Selects the initial swatch that matches the current variant.
   * Uses VariantStateManager to get the initial variant ID.
   */
  #selectInitialSwatch() {
    const currentVariantId = this.#getCurrentVariantId();
    if (!currentVariantId) return;

    const { variants, options } = this.#variantOptionsData || {};
    if (!variants || !options) return;

    const currentVariant = variants.find(v => v.id.toString() === currentVariantId);
    if (!currentVariant) return;

    const swatchInputs = this.querySelectorAll('input[type="radio"][name*="-swatch"]');
    if (swatchInputs.length === 0) return;

    // Get the swatch option name from the first input
    const firstSwatch = /** @type {HTMLInputElement} */ (swatchInputs[0]);
    const swatchName = firstSwatch.name?.split('-')[0];

    // Find the position of this option
    let swatchOptionPosition = null;
    for (const option of options) {
      if (option.name.toLowerCase() === swatchName?.toLowerCase()) {
        swatchOptionPosition = option.position;
        break;
      }
    }

    if (!swatchOptionPosition) return;

    // Get the value for this option from the current variant
    const swatchValue = /** @type {string|null|undefined} */ (
      currentVariant[/** @type {keyof VariantData} */ (`option${swatchOptionPosition}`)]
    );

    if (!swatchValue) return;

    // Find and select the swatch input with this value
    swatchInputs.forEach(input => {
      const swatchInput = /** @type {HTMLInputElement} */ (input);
      if (swatchInput.value === swatchValue) {
        swatchInput.checked = true;
        this.updateSelectedOption(swatchInput);
      }
    });
  }

  /**
   * Debounced handler for swatch hover - fetches and previews the variant.
   * @param {HTMLInputElement} swatchInput - The swatch input being hovered
   */
  #handleSwatchHoverDebounced(swatchInput) {
    // Don't preview if this swatch is already selected
    if (swatchInput.checked) return;

    // Don't preview if we're in the middle of selecting a variant
    if (this.#isSelectingVariant) return;

    // Store the original variant ID before we start previewing
    if (!this.isPreviewMode) {
      this.#originalVariantBeforePreview = this.#getCurrentVariantId();
    }

    // Reset carousel IMMEDIATELY before fetching to prevent scrolling glitch
    this.#resetCarouselToFirstImage();

    // Use VariantStateManager to find the correct variant
    let variantIdToPreview = this.#variantStateManager?.findVariantForSwatch(swatchInput, true);

    // Only fall back to dataset values if VariantStateManager doesn't exist or has no target gender data
    const hasTargetGender = this.#variantOptionsData?.targetGenderOptionPosition && this.#variantOptionsData?.targetGenderValue;

    if (!variantIdToPreview && (!this.#variantStateManager || !hasTargetGender)) {
      // Fallback to dataset values only when VariantStateManager can't help
      const availableCount = parseInt(swatchInput.dataset.availableCount || '0');
      const firstAvailableVariantId = swatchInput.dataset.firstAvailableOrFirstVariantId;
      const defaultVariantId = swatchInput.dataset.variantId;
      variantIdToPreview = availableCount > 0 ? firstAvailableVariantId : defaultVariantId;
      if (!variantIdToPreview) return;
    } else if (!variantIdToPreview && hasTargetGender) {
      // If we have target gender but VariantStateManager didn't find a variant, don't proceed
      // This prevents showing wrong variant when target gender should be preserved
      return;
    }

    // Store that we're in preview mode
    this.isPreviewMode = true;
    this.previewedSwatch = swatchInput;

    // Add preview class to the swatch label for visual feedback
    const swatchLabel = swatchInput.closest('label');
    if (swatchLabel) {
      swatchLabel.classList.add('swatch-preview');
    }

    // Directly preview the variant without fetching
    if (variantIdToPreview && this.parentProductCard instanceof ProductCard) {
      // Find the media ID for this variant
      const variantInput = this.querySelector(`input[data-variant-id="${variantIdToPreview}"]`);
      const mediaId = variantInput instanceof HTMLElement ? variantInput.dataset.optionMediaId : null;

      if (mediaId) {
        // Use the product card's existing preview functionality
        this.parentProductCard.previewVariant(mediaId);
      }
    }
  }

  /**
   * Handles mouse leaving a swatch.
   * @param {HTMLInputElement} swatchInput - The swatch input being left
   */
  #handleSwatchLeave(swatchInput) {
    // Cancel any pending hover
    this.#hoverDebounced?.cancel?.();

    // Don't interfere if we're selecting a variant
    if (this.#isSelectingVariant) {
      // Just remove preview classes and return
      const allSwatchLabels = this.querySelectorAll('label.swatch-preview');
      allSwatchLabels.forEach(label => label.classList.remove('swatch-preview'));
      return;
    }

    // Remove preview class from all swatch labels
    const allSwatchLabels = this.querySelectorAll('label.swatch-preview');
    allSwatchLabels.forEach(label => label.classList.remove('swatch-preview'));

    if (!this.isPreviewMode) return;

    this.isPreviewMode = false;
    this.previewedSwatch = null;

    // Reset the display to the selected variant immediately (not debounced)
    // This ensures the preview is cleared when definitively leaving the swatch area
    if (this.parentProductCard instanceof ProductCard) {
      this.parentProductCard.resetPreviewImmediately();
    }

    // Clear the stored original variant
    this.#originalVariantBeforePreview = null;
  }

  /**
   * Resets the carousel to the first image (scrollLeft = 0).
   * Always resets to first image on variant change (as per requirement).
   */
  #resetCarouselToFirstImage() {
    const carousel = this.parentProductCard?.querySelector('.slideshow__slider, .carousel__slider, [data-slideshow-slider]');
    if (carousel && carousel instanceof HTMLElement) {
      carousel.scrollLeft = 0;
      carousel.scrollTop = 0;
    }

    // Also reset slideshow to first slide if it exists
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.parentProductCard);
    const slideshow = productCard?.refs?.slideshow;
    if (slideshow && typeof slideshow.select === 'function') {
      const firstSlide = slideshow.slides?.[0];
      if (firstSlide) {
        const slideId = firstSlide.getAttribute('slide-id');
        if (slideId) {
          slideshow.select({ id: slideId }, undefined, { animate: false });
        }
      }
    }
  }

  /**
   * Gets the current variant ID from the product card link URL.
   * Falls back to VariantStateManager's initial variant ID if no variant is in the URL.
   * @returns {string | null} The current variant ID or null.
   */
  #getCurrentVariantId() {
    const productCardLink = this.parentProductCard?.querySelector('[ref="productCardLink"]');
    if (!(productCardLink instanceof HTMLAnchorElement)) return null;

    try {
      const url = new URL(productCardLink.href);
      const variantId = url.searchParams.get('variant');

      // If no variant in URL, use VariantStateManager to get initial variant
      if (!variantId && this.#variantStateManager) {
        return this.#variantStateManager.getInitialVariantId();
      }

      return variantId;
    } catch {
      // Fallback to VariantStateManager's initial variant ID if URL parsing fails
      if (this.#variantStateManager) {
        return this.#variantStateManager.getInitialVariantId();
      }
      return null;
    }
  }

  /**
   * Override fetchUpdatedSection to track when selection is complete.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // Call parent's fetchUpdatedSection for actual variant selection
    const result = super.fetchUpdatedSection(requestUrl, morphElementSelector);

    // Reset the selecting flag after a delay to ensure all updates are complete
    setTimeout(() => {
      this.#isSelectingVariant = false;
    }, 500);

    return result;
  }

  /**
   * Override updateVariantPicker to preserve swatch checked states during preview mode.
   * This prevents hover from auto-selecting swatches.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {{id: string, url: string} | undefined} Information about the new product if it has changed.
   */
  updateVariantPicker(newHtml) {
    // Use our tracked state instead of checking the DOM
    const wasExpanded = this.#overflowListExpanded;

    // If overflow list was expanded, modify the new HTML before morphing
    if (wasExpanded) {
      const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
      if (newVariantPickerSource) {
        const newOverflowList = newVariantPickerSource.querySelector('overflow-list');
        if (newOverflowList) {
          // Set the disabled attribute on the new HTML before morphing
          newOverflowList.setAttribute('disabled', 'true');
          // Also hide the placeholder to match the showAll() behavior
          const placeholder = newOverflowList.querySelector('[slot="overflow"]');
          if (placeholder instanceof HTMLElement) {
            placeholder.style.display = 'none';
            placeholder.style.width = '0';
          }
        }
      }
    }

    // If we're in preview mode, save the current checked states and previewed swatch info before morphing
    /** @type {Map<string, string> | null} */
    let savedCheckedStates = null;
    /** @type {{name: string, value: string} | null} */
    let previewedSwatchInfo = null;
    if (this.isPreviewMode && this.previewedSwatch) {
      const checkedStatesMap = new Map();
      const swatchInputs = this.querySelectorAll('input[type="radio"][name*="-swatch"]');
      swatchInputs.forEach(input => {
        const swatchInput = /** @type {HTMLInputElement} */ (input);
        if (swatchInput.checked) {
          checkedStatesMap.set(swatchInput.name, swatchInput.value);
        }
      });

      savedCheckedStates = checkedStatesMap;

      // Save previewed swatch info (name and value) so we can find it after morphing
      previewedSwatchInfo = {
        name: this.previewedSwatch.name,
        value: this.previewedSwatch.value
      };
    }

    // Call parent method to update the variant picker
    const newProduct = super.updateVariantPicker(newHtml);

    // Restore checked states after morphing if we're in preview mode
    if (this.isPreviewMode && savedCheckedStates !== null) {
      const swatchInputs = this.querySelectorAll('input[type="radio"][name*="-swatch"]');
      swatchInputs.forEach(input => {
        const swatchInput = /** @type {HTMLInputElement} */ (input);
        const savedValue = savedCheckedStates.get(swatchInput.name);
        // Only restore if this input matches the saved value
        if (savedValue && swatchInput.value === savedValue) {
          swatchInput.checked = true;
        } else {
          swatchInput.checked = false;
        }
      });

      // Re-apply preview class to the previewed swatch label and update reference
      if (previewedSwatchInfo) {
        const previewedInput = /** @type {HTMLInputElement | undefined} */ (
          Array.from(swatchInputs).find(
            input => {
              const inputEl = /** @type {HTMLInputElement} */ (input);
              return inputEl.name === previewedSwatchInfo.name && inputEl.value === previewedSwatchInfo.value;
            }
          )
        );
        if (previewedInput) {
          this.previewedSwatch = previewedInput;
          const previewedLabel = previewedInput.closest('label');
          if (previewedLabel) {
            previewedLabel.classList.add('swatch-preview');
          }
        }
      }
    }

    // Ensure the overflow list stays in the expanded state if it was expanded
    if (wasExpanded) {
      const currentOverflowList = this.refs.overflowList || this.querySelector('overflow-list');
      if (currentOverflowList && !currentOverflowList.hasAttribute('disabled')) {
        // If morph somehow removed the disabled state, re-apply it immediately
        currentOverflowList.setAttribute('disabled', 'true');
        const placeholder = currentOverflowList.querySelector('[slot="overflow"]');
        if (placeholder instanceof HTMLElement) {
          placeholder.style.display = 'none';
          placeholder.style.width = '0';
        }
      }
    }

    return newProduct;
  }


  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * Also preserves other option values (like target_gender) when changing one option.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Set flag to indicate we're selecting a variant (not previewing)
    this.#isSelectingVariant = true;

    // Cancel any pending hover previews immediately when clicking
    this.#hoverDebounced?.cancel?.();

    // If we're in preview mode, cancel it immediately before processing the click
    if (this.isPreviewMode) {
      // Remove preview classes
      const allSwatchLabels = this.querySelectorAll('label.swatch-preview');
      allSwatchLabels.forEach(label => label.classList.remove('swatch-preview'));

      // Reset preview state
      this.isPreviewMode = false;
      this.previewedSwatch = null;
      this.#originalVariantBeforePreview = null;
    }

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = /** @type {HTMLInputElement} */ (event.target);

    // For swatch inputs, try to preserve other option values (like target_gender) when changing color
    if (isSwatchInput && this.#variantStateManager) {
      // Don't reset carousel here - let the variant update handle it
      // This prevents the jump when moving backward through swatches
      // this.#resetCarouselToFirstImage();

      // Prevent the default handling since we're handling it ourselves
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Use VariantStateManager to find variant that preserves other options
      let variantIdToUse = this.#variantStateManager.findVariantForSwatch(clickedSwatch, true);

      // Only fall back to dataset values if VariantStateManager doesn't exist or has no target gender data
      const hasTargetGender = this.#variantOptionsData?.targetGenderOptionPosition && this.#variantOptionsData?.targetGenderValue;

      if (!variantIdToUse && !hasTargetGender) {
        // Fallback to dataset values only when no target gender is involved
        const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
        const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;
        const defaultVariantId = clickedSwatch.dataset.variantId;
        variantIdToUse = (availableCount > 0 ? firstAvailableVariantId : defaultVariantId) || null;
      } else if (!variantIdToUse && hasTargetGender) {
        // If we have target gender but VariantStateManager didn't find a variant, don't proceed
        // This prevents showing wrong variant when target gender should be preserved
        super.variantChanged(event);
        return;
      }

      if (!variantIdToUse) {
        // No variant found, fall back to default behavior
        super.variantChanged(event);
        return;
      }

      // Build request URL with the variant that preserves other options
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl || !variantIdToUse) return;

      // Before fetching, immediately update the display to prevent flicker
      // Find the media for the new variant and show it right away
      const variantInput = this.querySelector(`input[data-variant-id="${variantIdToUse}"]`);
      const mediaId = variantInput instanceof HTMLElement ? variantInput.dataset.optionMediaId : null;

      if (mediaId && this.parentProductCard instanceof ProductCard) {
        // Immediately show the new variant's image (no animation)
        const slideshow = this.parentProductCard.refs?.slideshow;
        if (slideshow && typeof slideshow.select === 'function') {
          slideshow.select({ id: mediaId }, undefined, { animate: false });
        }
      }

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantIdToUse);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = variantIdToUse;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
      // Track that we've expanded the list
      this.#overflowListExpanded = true;
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

